import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Scale } from "lucide-react";
import SEO from "@/components/SEO";
import Navbar from "@/components/Navbar";
import FadeInSection from "@/components/FadeInSection";
import InlineCTA from "@/components/InlineCTA";
import LazySection from "@/components/LazySection";
import NewsletterBanner from "@/components/NewsletterBanner";
import { legalPages } from "@/data/platformPages";
import { useSanityLegalPages } from "@/hooks/useSanityContent";

const LegalPage = () => {
  const { slug: paramSlug } = useParams<{ slug: string }>();
  const slug = paramSlug || window.location.pathname.replace(/^\/|\/$/g, "");
  const { data: sanityPages } = useSanityLegalPages();

  const sanityPage = sanityPages?.find((p) => p.slug?.current === slug);
  const hardcodedPage = legalPages.find((p) => p.slug === slug);

  // For legal pages, Sanity uses portable text body instead of sections
  const page = sanityPage
    ? {
        title: sanityPage.title,
        description: hardcodedPage?.description ?? "",
        sections: hardcodedPage?.sections ?? [],
        slug,
      }
    : hardcodedPage;

  if (!page) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-20 text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">Page not found</h1>
          <Link to="/legal" className="text-primary hover:underline">Back to Legal</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO title={page.title} description={page.description} canonical={`/${page.slug}`} breadcrumbs={[{ name: "Home", href: "/" }, { name: "Legal", href: "/legal" }, { name: page.title, href: `/${page.slug}` }]} />
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-12 md:py-16">
        <div className="mb-8">
          <InlineCTA variant="newsletter" />
        </div>

        <FadeInSection>
          <Link to="/legal" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Legal
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <Scale className="w-8 h-8 text-primary" />
            <h1 className="text-3xl md:text-4xl font-extrabold text-foreground">{page.title}</h1>
          </div>
          <p className="text-muted-foreground text-lg max-w-3xl mb-10">{page.description}</p>
        </FadeInSection>

        {page.sections.map((section, i) => (
          <FadeInSection key={i} delay={0.05 + i * 0.03}>
            <div className="mb-8">
              <h2 className="text-xl font-bold text-foreground mb-3">{section.heading}</h2>
              <p className="text-base text-muted-foreground leading-relaxed">{section.content}</p>
            </div>
          </FadeInSection>
        ))}

        <FadeInSection delay={0.25}>
          <div className="mt-8 rounded-xl border border-border bg-card p-6">
            <p className="text-sm text-muted-foreground leading-relaxed">
              <strong className="text-foreground">Disclaimer:</strong> The information on this page is for educational purposes only and does not constitute legal or tax advice. Prediction market regulations vary by jurisdiction and are evolving rapidly. Consult a qualified professional for advice specific to your situation.
            </p>
          </div>
        </FadeInSection>

        <div className="mt-10">
          <LazySection><NewsletterBanner /></LazySection>
        </div>
      </div>
    </div>
  );
};

export default LegalPage;
